import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle2, Mail, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(false); // Default to signup
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const navigate = useNavigate();

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter email, 2 = enter code + new password
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  // Signup form state
  const [signupData, setSignupData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setVerificationSent(false);
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email: loginEmail,
        password: loginPassword
      });

      if (response.data.status === 'success') {
        const user = response.data.user;
        localStorage.setItem('casitaplex-user', JSON.stringify(user));
        onLogin(user);
        navigate('/dashboard');
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(detail);
      if (status === 403 && detail.toLowerCase().includes('verify')) {
        setNeedsVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendLoginVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification?email=${encodeURIComponent(loginEmail)}`);
      setVerificationSent(true);
      setError('');
    } catch (err) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/request-password-reset?email=${encodeURIComponent(forgotEmail)}`);
      setForgotStep(2);
      setForgotMessage('A 6-digit reset code has been sent to your email.');
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Failed to send reset code. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    if (newPassword.length < 6) {
      setForgotError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setForgotError('Passwords do not match.');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password?email=${encodeURIComponent(forgotEmail)}&code=${encodeURIComponent(resetCode)}&new_password=${encodeURIComponent(newPassword)}`);
      setForgotMessage('Password reset successfully! You can now log in.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotStep(1);
        setForgotEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');
        setForgotMessage('');
        setForgotError('');
        setIsLogin(true);
      }, 2000);
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Failed to reset password. Check your code and try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (signupData.password !== signupData.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    if (signupData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        first_name: signupData.first_name,
        last_name: signupData.last_name,
        username: signupData.username,
        email: signupData.email,
        password: signupData.password,
      });

      if (response.data.status === 'success') {
        setSignupSuccess(true);
        setSignupEmail(signupData.email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    setResendMsg('');
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification?email=${encodeURIComponent(signupEmail)}`);
      setResendMsg('Verification email sent! Check your inbox.');
    } catch {
      setResendMsg('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Password match indicator
  const passwordsEntered = signupData.password.length > 0 && signupData.confirm_password.length > 0;
  const passwordsMatch = signupData.password === signupData.confirm_password;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 via-black to-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-6 lg:gap-16">
        {/* Left Side - Branding */}
        <div className="text-center lg:text-left lg:flex-1">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-red-600 mb-3 sm:mb-4" data-testid="auth-logo">CASITAPLEX</h1>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-300">
            Connect with friends and the world around you on Casitaplex.
          </p>
        </div>

        {/* Right Side - Auth Form */}
        <div className="w-full max-w-md">
          {/* ===== SIGNUP SUCCESS / CHECK EMAIL ===== */}
          {signupSuccess ? (
            <div className="bg-zinc-800 rounded-lg shadow-2xl p-6 text-center" data-testid="signup-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-600/20 flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Check Your Email!</h2>
              <p className="text-gray-400 text-sm mb-1">We've sent a verification link to:</p>
              <p className="text-white font-semibold mb-4" data-testid="signup-email">{signupEmail}</p>
              <p className="text-gray-500 text-xs mb-4">
                Click the link in the email to activate your account. Your account will remain inactive until verified.
              </p>

              <div className="space-y-3">
                <Button
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="w-full bg-zinc-700 hover:bg-zinc-600 text-white h-10 text-sm"
                  data-testid="resend-verification-btn"
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </Button>
                {resendMsg && (
                  <p className={`text-xs ${resendMsg.includes('sent') ? 'text-green-400' : 'text-red-400'}`}>{resendMsg}</p>
                )}
                <hr className="border-zinc-600" />
                <Button
                  onClick={() => { setIsLogin(true); setSignupSuccess(false); setError(''); }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-10"
                  data-testid="goto-login-btn"
                >
                  Go to Login
                </Button>
              </div>
            </div>
          ) : isLogin ? (
            /* ===== LOGIN FORM ===== */
            <div className="bg-zinc-800 rounded-lg shadow-2xl p-5 sm:p-6" data-testid="login-form">
              <form onSubmit={handleLogin} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="bg-zinc-700 border-zinc-600 text-white h-11 sm:h-12 text-sm sm:text-base"
                  required
                  data-testid="login-email"
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="bg-zinc-700 border-zinc-600 text-white h-11 sm:h-12 text-sm sm:text-base"
                  required
                  data-testid="login-password"
                />

                {error && (
                  <div className="flex items-start gap-2 bg-red-900/20 border border-red-800/40 rounded-lg p-3" data-testid="auth-error">
                    <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {needsVerification && !verificationSent && (
                  <Button
                    type="button"
                    onClick={handleResendLoginVerification}
                    disabled={resendingVerification}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold h-10"
                    data-testid="resend-verification-btn"
                  >
                    {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                )}

                {verificationSent && (
                  <div className="bg-green-900/20 border border-green-800/40 rounded-lg p-3">
                    <p className="text-green-400 text-sm">Verification email sent! Check your inbox and spam folder, then try logging in again.</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-11 sm:h-12 text-base sm:text-lg"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Logging in...' : 'Log In'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setForgotEmail(loginEmail); setError(''); }}
                    className="text-red-500 hover:underline text-sm bg-transparent border-none cursor-pointer"
                    data-testid="forgot-password-link"
                  >
                    Forgotten password?
                  </button>
                </div>

                <hr className="border-zinc-600" />

                <div className="text-center">
                  <Button
                    type="button"
                    onClick={() => { setIsLogin(false); setError(''); }}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 h-10 sm:h-11"
                    data-testid="goto-signup-btn"
                  >
                    Create New Account
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            /* ===== SIGNUP FORM ===== */
            <div className="bg-zinc-800 rounded-lg shadow-2xl p-5 sm:p-6" data-testid="signup-form">
              <div className="text-center mb-3 sm:mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Sign Up</h2>
                <p className="text-gray-400 text-xs sm:text-sm">It's quick and easy.</p>
              </div>

              <hr className="border-zinc-600 mb-3 sm:mb-4" />

              <form onSubmit={handleSignup} className="space-y-2.5 sm:space-y-3">
                <div className="flex gap-2 sm:gap-3">
                  <Input
                    type="text"
                    placeholder="First name"
                    value={signupData.first_name}
                    onChange={(e) => setSignupData({ ...signupData, first_name: e.target.value })}
                    className="bg-zinc-700 border-zinc-600 text-white h-10 text-sm"
                    required
                    data-testid="signup-firstname"
                  />
                  <Input
                    type="text"
                    placeholder="Last name"
                    value={signupData.last_name}
                    onChange={(e) => setSignupData({ ...signupData, last_name: e.target.value })}
                    className="bg-zinc-700 border-zinc-600 text-white h-10 text-sm"
                    required
                    data-testid="signup-lastname"
                  />
                </div>

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                  <Input
                    type="text"
                    placeholder="username"
                    value={signupData.username}
                    onChange={(e) => setSignupData({ ...signupData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    className="bg-zinc-700 border-zinc-600 text-white pl-8 h-10 text-sm"
                    required
                    minLength={3}
                    data-testid="signup-username"
                  />
                </div>

                <Input
                  type="email"
                  placeholder="Email address"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  className="bg-zinc-700 border-zinc-600 text-white h-10 text-sm"
                  required
                  data-testid="signup-email"
                />

                <Input
                  type="password"
                  placeholder="New password"
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  className="bg-zinc-700 border-zinc-600 text-white h-10 text-sm"
                  required
                  minLength={6}
                  data-testid="signup-password"
                />

                <div className="relative">
                  <Input
                    type="password"
                    placeholder="Confirm password"
                    value={signupData.confirm_password}
                    onChange={(e) => setSignupData({ ...signupData, confirm_password: e.target.value })}
                    className={`bg-zinc-700 border-zinc-600 text-white h-10 text-sm pr-10 ${
                      passwordsEntered ? (passwordsMatch ? 'border-green-600' : 'border-red-600') : ''
                    }`}
                    required
                    minLength={6}
                    data-testid="signup-confirm-password"
                  />
                  {passwordsEntered && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {passwordsMatch ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" data-testid="password-match-icon" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-500" data-testid="password-mismatch-icon" />
                      )}
                    </div>
                  )}
                </div>
                {passwordsEntered && !passwordsMatch && (
                  <p className="text-red-400 text-xs" data-testid="password-mismatch-text">Passwords do not match</p>
                )}

                {error && (
                  <div className="flex items-start gap-2 bg-red-900/20 border border-red-800/40 rounded-lg p-2.5" data-testid="auth-error">
                    <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <p className="text-gray-500 text-[10px] sm:text-xs">
                  By clicking Sign Up, you agree to our Terms, Privacy Policy and Cookies Policy.
                </p>

                <div className="text-center pt-1 sm:pt-2">
                  <Button
                    type="submit"
                    disabled={loading || (passwordsEntered && !passwordsMatch)}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-12 sm:px-16 h-10"
                    data-testid="signup-submit-btn"
                  >
                    {loading ? 'Creating...' : 'Sign Up'}
                  </Button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setIsLogin(true); setError(''); }}
                    className="text-red-500 hover:underline text-sm"
                    data-testid="goto-login-link"
                  >
                    Already have an account?
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Back to streaming */}
          {!signupSuccess && (
            <div className="text-center mt-4">
              <a href="/browse" className="text-gray-400 hover:text-white text-xs sm:text-sm" data-testid="back-to-streaming">
                ← Back to CASITAPLEX Streaming
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Forgot Password Overlay */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="forgot-password-overlay">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md p-6 sm:p-8 relative">
            <button
              onClick={() => { setShowForgotPassword(false); setForgotStep(1); setForgotError(''); setForgotMessage(''); }}
              className="absolute top-3 right-3 text-gray-500 hover:text-white"
              data-testid="forgot-password-close"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <h2 className="text-xl font-bold text-white mb-1">Reset Password</h2>
            <p className="text-gray-400 text-sm mb-6">
              {forgotStep === 1 ? "Enter your email to receive a reset code." : "Enter the 6-digit code sent to your email."}
            </p>

            {forgotMessage && (
              <div className="flex items-start gap-2 bg-green-900/20 border border-green-800/40 rounded-lg p-2.5 mb-4" data-testid="forgot-success-msg">
                <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-green-400 text-sm">{forgotMessage}</p>
              </div>
            )}

            {forgotError && (
              <div className="flex items-start gap-2 bg-red-900/20 border border-red-800/40 rounded-lg p-2.5 mb-4" data-testid="forgot-error-msg">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{forgotError}</p>
              </div>
            )}

            {forgotStep === 1 && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-sm">Email Address</Label>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    className="bg-zinc-800 border-zinc-600 text-white h-11 mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-11"
                  data-testid="forgot-send-code-btn"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
            )}

            {forgotStep === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-sm">6-Digit Code</Label>
                  <Input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="bg-zinc-800 border-zinc-600 text-white h-11 mt-1 text-center tracking-widest text-lg font-mono"
                    data-testid="forgot-code-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Enter new password"
                    className="bg-zinc-800 border-zinc-600 text-white h-11 mt-1"
                    data-testid="forgot-new-password-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    placeholder="Confirm new password"
                    className="bg-zinc-800 border-zinc-600 text-white h-11 mt-1"
                    data-testid="forgot-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading || resetCode.length !== 6}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-11"
                  data-testid="forgot-reset-btn"
                >
                  {forgotLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setForgotStep(1); setForgotError(''); setForgotMessage(''); }}
                  className="w-full text-gray-400 hover:text-white text-sm py-2"
                  data-testid="forgot-back-to-email"
                >
                  ← Back to enter email
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
